export interface TimeOptions {
  timeScale?: number;
  fps?: number;
}

/** A time class for managing the passage of time from frame to frame within an odie scene. */
export class Time {
  /**
   * the time modifier that defaults to one. Speed up or slow down time
   * by modifying this value
   */
  public timeScale = 1;

  /**
   * the maximum size a delta step can be. 20fps. If your game runs slower than this.. this
   * we are doing something wrong!
   */
  public maxDeltaTime = 1000 / 20;

  /** the target FPS for this game. 60fps is the default as thats what most devices hit */
  protected _fps = 60;
  protected _lastTime: number;
  protected _frameDuration: number;
  protected _deltaTime = 0;
  protected _frameTime = 0;

  constructor(options: TimeOptions = {}) {
    this.timeScale = options.timeScale ?? 1;
    this._lastTime = performance.now();
    this.fps = options.fps ?? 60;
    this._frameDuration = 1000 / this._fps;
  }

  /** call at the start of the game update loop in the scene */
  public nextUpdate(): void {
    const now = performance.now();

    this._deltaTime = Math.min(now - this._lastTime, this.maxDeltaTime);
    this._frameTime = this._deltaTime / this._frameDuration;

    this._lastTime = now;
  }

  /** the time elapsed from the previous frame in milliseconds */
  public get deltaTime(): number {
    return this._deltaTime * this.timeScale;
  }

  /**
   * the time elapsed from the previous frame in milliseconds
   * does not take into account the time scale
   */
  public get unscaledDeltaTime(): number {
    return this._deltaTime;
  }

  /**
   * the time elapsed from the previous frame in frames.
   * a value around one indicates we are matching the frame rate
   */
  public get frameTime(): number {
    return this._frameTime * this.timeScale;
  }

  /**
   * the time elapsed from the previous frame in frames.
   * a value around one indicates we are matching the frame rate
   * does not take into account the time scale
   */
  public get unscaledFrameTime(): number {
    return this._frameTime;
  }

  /** Get the target frame rate (frame per second) to used to calculate frameTime */
  public get fps(): number {
    return this._fps;
  }

  /** Set the target frame rate (frame per second) to used to calculate frameTime */
  public set fps(v: number) {
    this._fps = v;
    this._frameDuration = 1000 / this._fps;
  }
}
